import { InfoCircleOutlined } from "@ant-design/icons"
import { Button, Col, Flex, Form, Row, Space, Switch, Tooltip, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useEffect, useMemo } from "react"

import InputNumber from "../../../../components/Input/InputNumber"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput"
import SelectComponent from "../../../../components/Select"
import { placeholders } from "../../../../constants/placeholders"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone"
import { fetchSubcontractorsFilterOptions } from "../../redux/subcontractors.thunks"
import { type SubcontractorFormData } from "./subcontractor-form-data.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const { Text } = Typography

interface SubcontractorFormProps {
  loading?: boolean
  initialValues?: Partial<SubcontractorFormData>
  /** The name a caller creating inline (e.g. from a finance row) already typed. */
  defaultName?: string
  innerForm?: FormInstance
  onSave: (data: SubcontractorFormData) => void
  onCancel: () => void
}

export const SubcontractorForm = ({
  loading,
  initialValues,
  defaultName,
  innerForm,
  onSave,
  onCancel
}: SubcontractorFormProps) => {
  const dispatch = useAppDispatch()
  const [form] = Form.useForm<SubcontractorFormData>(innerForm)

  const fetching = useAppSelector((state) => state.subcontractors.saveFetching)
  const filterOptions = useAppSelector(
    (state) => state.subcontractors.filterOptions
  )

  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )

  const inviteSubcontractors = permissions?.features?.subcontractor_invite
  const createSubcontractors = permissions?.subcontractors?.subcontractor_create

  const defaultSendInvitation = useMemo(() => {
    if (inviteSubcontractors && !createSubcontractors) return true
    return !(!inviteSubcontractors && createSubcontractors)
  }, [createSubcontractors, inviteSubcontractors])

  const showToggle =
    !initialValues && inviteSubcontractors && createSubcontractors

  const sendInvitation = Form.useWatch("send_invitation", form)

  const onFinish = (values: SubcontractorFormData) => {
    onSave(values)
  }

  useEffect(() => {
    dispatch(fetchSubcontractorsFilterOptions())
  }, [dispatch])

  return (
    <Form<SubcontractorFormData>
      form={form}
      layout="vertical"
      onFinish={onFinish}
      validateTrigger={["onSubmit", "onChange"]}
      className="with-default-error"
      initialValues={
        initialValues
          ? initialValues
          : { send_invitation: defaultSendInvitation, first_name: defaultName }
      }
    >
      <Flex
        vertical
        className="pb-4 overflow-auto overflow-x-hidden tailwind-scroll max-h-[75vh]"
      >
        {/* Name Fields */}
        <Row gutter={12}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormData>
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter subcontractor first name"
                }
              ]}
            >
              <TextInput placeholder="Enter first name" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormData>
              label="Middle Name"
              name="middle_name"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: "Please enter subcontractor middle name"
                }
              ]}
            >
              <TextInput placeholder="Enter middle name" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormData>
              label="Last Name"
              name="last_name"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: "Please enter subcontractor last name"
                }
              ]}
            >
              <TextInput placeholder="Enter last name" size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Fields */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormData>
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" }
              ]}
              normalize={normalizeEmail}
            >
              <TextInput placeholder={placeholders.email} size="large" disableAutoCapitalize />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormData>
              label="Phone Number"
              name="phone"
              rules={[{ required: false, validator: validatePhone }]}
            >
              <PhoneInput placeholder={placeholders.phone} size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Price, Locale & Specialization */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormData>
              label="Price per hour"
              name="price_per_hour"
              rules={[
                { required: false, message: "Please enter your price" },
                {
                  type: "number",
                  min: 0,
                  message: "Value must be positive"
                }
              ]}
            >
              <InputNumber
                size="large"
                prefix="$"
                style={{ width: "100%" }}
                placeholder="0"
                min={0}
                precision={2}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormData>
              label="Location"
              name="location"
              rules={[
                {
                  required: false,
                  message: "Please select location"
                }
              ]}
            >
              <SelectComponent
                size="large"
                mode={"multiple"}
                maxTagCount={"responsive"}
                placeholder="Select location"
                options={filterOptions?.["location"] || []}
                optionFilterType="form"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input?.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item<SubcontractorFormData>
          label="Specialization"
          name="specialization"
          rules={[
            {
              required: false,
              message: "Please select subcontractor specialization"
            }
          ]}
        >
          <SelectComponent
            size="large"
            placeholder="Select custom specialization"
            options={filterOptions?.["specialization"] || []}
            optionFilterType="form"
            mode="multiple"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item hidden={!showToggle}>
          <Space>
            <Form.Item<SubcontractorFormData>
              noStyle
              name="send_invitation"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Text>Send invitation</Text>
            <Tooltip title="If disabled, the subcontractor will be added without invitation.">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        </Form.Item>
      </Flex>

      {/* Form Actions */}
      <Form.Item noStyle>
        <Flex align="center" justify="flex-end" gap="middle">
          <Button size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            loading={fetching || loading}
            className="min-w-44"
          >
            {initialValues
              ? "Save Changes"
              : sendInvitation
                ? "Send Invitation"
                : "Add Subcontractor"}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  )
}
